import React from 'react';
import { History, Loader2 } from 'lucide-react';
import { ADJUSTMENT_REASONS } from '@/lib/stockControl';

const reasonLabel = (value) => ADJUSTMENT_REASONS.find((r) => r.value === value)?.label || value;

function fmtTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Every stock movement made from the Stock Control area, newest first, with who
// made it and why.
export default function StockAuditLog({ adjustments, loading }) {
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!adjustments.length) {
    return (
      <div className="border border-white/10 bg-[#111115] p-12 text-center text-white/30">
        <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p className="text-xs font-bold tracking-widest">NO ADJUSTMENTS RECORDED YET</p>
        <p className="text-[11px] mt-1">Stock adjustments made here will be logged with the admin and reason</p>
      </div>
    );
  }

  return (
    <div className="border border-white/10 bg-[#111115] overflow-x-auto">
      <table className="w-full text-xs min-w-[880px]">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-white/30 font-bold text-left py-3 px-4">When</th>
            <th className="text-white/30 font-bold text-left py-3 px-3">Product</th>
            <th className="text-white/30 font-bold text-left py-3 px-3">SKU</th>
            <th className="text-white/30 font-bold text-center py-3 px-3">Change</th>
            <th className="text-white/30 font-bold text-center py-3 px-3">Level</th>
            <th className="text-white/30 font-bold text-left py-3 px-3">Reason</th>
            <th className="text-white/30 font-bold text-left py-3 px-3">Admin</th>
          </tr>
        </thead>
        <tbody>
          {adjustments.map((a) => (
            <tr key={a.id} className="border-b border-white/5">
              <td className="py-3 px-4 text-white/50 whitespace-nowrap">{fmtTime(a.created_date)}</td>
              <td className="py-3 px-3">
                <p className="text-white font-bold">{a.product_name || '—'}</p>
                {a.variation_name && <p className="text-white/40 text-[11px] mt-0.5">{a.variation_name}</p>}
                {a.supplier && (
                  <p className="text-white/40 text-[11px] mt-0.5">
                    {a.supplier}
                    {a.receipt_reference ? ` · ${a.receipt_reference}` : ''}
                    {a.unit_cost_ex_vat != null ? ` · £${Number(a.unit_cost_ex_vat).toFixed(2)}/unit` : ''}
                    {a.batch_reference ? ` · ${a.batch_reference}` : ''}
                  </p>
                )}
                {a.note && <p className="text-white/30 text-[11px] mt-0.5 italic">{a.note}</p>}
              </td>
              <td className="py-3 px-3 text-white/60 font-mono">{a.sku || '—'}</td>
              <td className="py-3 px-3 text-center">
                <span className={`font-bold ${Number(a.quantity_change) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {Number(a.quantity_change) > 0 ? `+${a.quantity_change}` : a.quantity_change}
                </span>
              </td>
              <td className="py-3 px-3 text-center text-white/70 whitespace-nowrap">
                {a.quantity_before} <span className="text-white/25">→</span> <span className="text-white font-bold">{a.quantity_after}</span>
              </td>
              <td className="py-3 px-3">
                <span className="inline-block px-2 py-0.5 border border-white/15 text-white/60 text-[10px] font-bold tracking-widest">
                  {String(reasonLabel(a.reason)).toUpperCase()}
                </span>
              </td>
              <td className="py-3 px-3 text-white/50">{a.admin_name || a.admin_email || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}